/**
 * DebugOverlay — the instrument panel: per-frame budget readouts plus a live
 * frame-time trace, which is the thing SPEC §18's "flat graph, no GC sawtooth"
 * acceptance criterion is actually judged against.
 */

import { TOKENS } from '../voxel/Palette.ts';

export interface DebugSnapshot {
  fps: number; frameMs: number; updateMs: number; renderMs: number; steps: number;
  history: Float32Array; historyHead: number;
  drawCalls: number; triangles: number; programs: number; geometries: number;
  mode: string;
  rows: Array<[string, string]>;
}

/* Graph geometry. Purely internal to this widget, so it lives here rather than
   in tuning.ts — nothing else in the game can observe these. */
const COLS = 120;
const COL_W = 2;
const GRAPH_W = COLS * COL_W;
const GRAPH_H = 58;
/* Fixed vertical scale. An auto-scaling graph normalises a sawtooth away and
   makes every trace look equally flat, which defeats the entire point. */
const SCALE_MS = 50;
const MS_60 = 1000 / 60;
const MS_30 = 1000 / 30;

function rgba(hex: number, a: number): string {
  return `rgba(${(hex >> 16) & 255},${(hex >> 8) & 255},${hex & 255},${a})`;
}

const C_BG      = rgba(TOKENS.abyss, 0.55);
const C_BAND    = rgba(TOKENS.verdigris, 0.07);
const C_OK      = rgba(TOKENS.verdigris, 1);
const C_WARN    = rgba(TOKENS.corrode, 1);
const C_BAD     = rgba(TOKENS.alarm, 1);
const C_LINE_60 = rgba(TOKENS.caustic, 0.30);
const C_LINE_30 = rgba(TOKENS.corrode, 0.36);
const C_LABEL   = rgba(TOKENS.bone, 0.34);
const C_PEAK    = rgba(TOKENS.caustic, 0.72);
const C_FRAME   = rgba(TOKENS.caustic, 0.16);
const GRAPH_FONT = '8px ui-monospace, Consolas, monospace';

const LABELS = [
  'FPS', 'FRAME MS', 'UPDATE MS', 'RENDER MS', 'STEPS',
  'DRAW CALLS', 'TRIANGLES', 'PROGRAMS', 'GEOMETRIES',
];
const FIXED = LABELS.length;

const R_FPS = 0, R_FRAME = 1, R_UPDATE = 2, R_RENDER = 3, R_STEPS = 4,
      R_DRAWS = 5, R_TRIS = 6, R_PROGS = 7, R_GEOS = 8;

/** Thousands grouping without `toLocaleString` (which is slow and locale-variant). */
function groupInt(n: number): string {
  const s = String(n);
  if (s.length <= 3) return s;
  let out = '';
  for (let i = s.length, c = 0; i > 0; i--) {
    out = s.charAt(i - 1) + out;
    if (++c % 3 === 0 && i > 1) out = ',' + out;
  }
  return out;
}

function mk<K extends keyof HTMLElementTagNameMap>(
  tag: K, cls: string, parent?: HTMLElement,
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  e.className = cls;
  if (parent) parent.appendChild(e);
  return e;
}

export class DebugOverlay {
  private readonly root: HTMLDivElement;
  private readonly modeEl: HTMLSpanElement;
  private readonly rowsEl: HTMLDivElement;
  private readonly sepEl: HTMLDivElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D | null;

  private readonly keyEls: HTMLSpanElement[] = [];
  private readonly valEls: HTMLSpanElement[] = [];
  private readonly keyCache: string[] = [];
  private readonly valCache: string[] = [];
  /** Last written value, pre-formatting. -1 is unreachable for every metric. */
  private readonly numCache = new Float64Array(FIXED).fill(-1);

  private shown = FIXED;
  private sepShown = false;
  private modeCache = '';
  private peakQ = -1;
  private peakText = 'PK --';
  private _open = false;

  constructor(parent: HTMLElement) {
    this.root = mk('div', 'dbg', parent);

    const head = mk('div', 'dbg-head', this.root);
    mk('span', 'dbg-blip', head);
    mk('span', 'dbg-title', head).textContent = 'DIAGNOSTICS';
    this.modeEl = mk('span', 'dbg-mode', head);
    this.modeEl.textContent = '--';

    this.canvas = mk('canvas', 'dbg-graph', this.root);
    /* Cap DPR at 2: beyond that the backing store cost buys nothing on a
       58px strip, and the profiler must never be the thing that costs frames. */
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width = Math.round(GRAPH_W * dpr);
    this.canvas.height = Math.round(GRAPH_H * dpr);
    this.ctx = this.canvas.getContext('2d', { alpha: true });
    if (this.ctx) {
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      this.ctx.textBaseline = 'bottom';
    }

    this.rowsEl = mk('div', 'dbg-rows', this.root);
    this.grow(FIXED);
    for (let i = 0; i < FIXED; i++) {
      this.keyCache[i] = LABELS[i];
      this.keyEls[i].textContent = LABELS[i];
    }
    /* Sits between the fixed metrics and the caller rows; every later row slot
       is appended after it, so the split holds without reordering the grid. */
    this.sepEl = mk('div', 'dbg-sep', this.rowsEl);
    this.sepEl.style.display = 'none';
  }

  get open(): boolean { return this._open; }

  toggle(): void {
    this._open = !this._open;
    this.root.classList.toggle('is-open', this._open);
  }

  update(s: DebugSnapshot): void {
    if (!this._open) return;

    this.writeFixed(R_FPS, s.fps, 1);
    this.writeFixed(R_FRAME, s.frameMs, 2);
    this.writeFixed(R_UPDATE, s.updateMs, 2);
    this.writeFixed(R_RENDER, s.renderMs, 2);
    this.writeInt(R_STEPS, s.steps);
    this.writeInt(R_DRAWS, s.drawCalls);
    this.writeInt(R_TRIS, s.triangles);
    this.writeInt(R_PROGS, s.programs);
    this.writeInt(R_GEOS, s.geometries);

    if (s.mode !== this.modeCache) {
      this.modeCache = s.mode;
      this.modeEl.textContent = s.mode;
    }

    const extra = s.rows.length;
    const total = FIXED + extra;
    this.grow(total);
    for (let i = 0; i < extra; i++) {
      const j = FIXED + i;
      const row = s.rows[i];
      if (this.keyCache[j] !== row[0]) {
        this.keyCache[j] = row[0];
        this.keyEls[j].textContent = row[0];
      }
      if (this.valCache[j] !== row[1]) {
        this.valCache[j] = row[1];
        this.valEls[j].textContent = row[1];
      }
    }
    if (total !== this.shown) {
      const n = this.keyEls.length;
      for (let i = Math.min(this.shown, total); i < n; i++) {
        const vis = i < total ? '' : 'none';
        this.keyEls[i].style.display = vis;
        this.valEls[i].style.display = vis;
      }
      this.shown = total;
    }
    if ((extra > 0) !== this.sepShown) {
      this.sepShown = extra > 0;
      this.sepEl.style.display = this.sepShown ? '' : 'none';
    }

    this.drawGraph(s.history, s.historyHead);
  }

  dispose(): void {
    this.root.remove();
    this.keyEls.length = 0;
    this.valEls.length = 0;
    this.keyCache.length = 0;
    this.valCache.length = 0;
  }

  /** Append row slots until the pool holds `total`. Only ever runs on growth. */
  private grow(total: number): void {
    while (this.keyEls.length < total) {
      const k = mk('span', 'dbg-k', this.rowsEl);
      const v = mk('span', 'dbg-v', this.rowsEl);
      this.keyEls.push(k);
      this.valEls.push(v);
      this.keyCache.push('');
      this.valCache.push('');
    }
  }

  /* Quantise to display precision *before* comparing, so a value that is only
     changing below the visible digit costs neither a string nor a DOM write. */
  private writeFixed(i: number, v: number, decimals: number): void {
    const m = decimals === 2 ? 100 : 10;
    const q = Math.round(v * m);
    if (this.numCache[i] === q) return;
    this.numCache[i] = q;
    const s = (q / m).toFixed(decimals);
    this.valCache[i] = s;
    this.valEls[i].textContent = s;
  }

  private writeInt(i: number, v: number): void {
    const q = v | 0;
    if (this.numCache[i] === q) return;
    this.numCache[i] = q;
    const s = groupInt(q);
    this.valCache[i] = s;
    this.valEls[i].textContent = s;
  }

  private drawGraph(h: Float32Array, head: number): void {
    const ctx = this.ctx;
    const n = h.length;
    if (!ctx || n === 0) return;

    ctx.fillStyle = C_BG;
    ctx.fillRect(0, 0, GRAPH_W, GRAPH_H);

    const y60 = GRAPH_H - (MS_60 / SCALE_MS) * GRAPH_H;
    const y30 = GRAPH_H - (MS_30 / SCALE_MS) * GRAPH_H;

    /* Tint the in-budget band so "flat and green" is a single glance, not a read. */
    ctx.fillStyle = C_BAND;
    ctx.fillRect(0, y60, GRAPH_W, GRAPH_H - y60);

    let peak = 0;
    let fill = '';
    for (let i = 0; i < COLS; i++) {
      /* `head` is the next write slot, so the newest sample is head-1. */
      const ms = h[(head - COLS + i + n + n) % n];
      if (!(ms > 0)) continue;
      if (ms > peak) peak = ms;
      const c = ms < MS_60 ? C_OK : ms < MS_30 ? C_WARN : C_BAD;
      if (c !== fill) { fill = c; ctx.fillStyle = c; }
      const bh = (ms < SCALE_MS ? ms / SCALE_MS : 1) * GRAPH_H;
      ctx.fillRect(i * COL_W, GRAPH_H - bh, COL_W, bh);
    }

    ctx.fillStyle = C_LINE_30;
    ctx.fillRect(0, Math.round(y30), GRAPH_W, 1);
    ctx.fillStyle = C_LINE_60;
    ctx.fillRect(0, Math.round(y60), GRAPH_W, 1);

    ctx.font = GRAPH_FONT;
    ctx.fillStyle = C_LABEL;
    ctx.fillText('33.3', 3, y30 - 2);
    ctx.fillText('16.7', 3, y60 - 2);

    const pq = Math.round(peak * 10);
    if (pq !== this.peakQ) {
      this.peakQ = pq;
      this.peakText = 'PK ' + (pq / 10).toFixed(1);
    }
    ctx.textAlign = 'right';
    ctx.fillStyle = peak >= MS_30 ? C_BAD : peak >= MS_60 ? C_WARN : C_PEAK;
    ctx.fillText(this.peakText, GRAPH_W - 3, 11);
    ctx.textAlign = 'left';

    ctx.strokeStyle = C_FRAME;
    ctx.lineWidth = 1;
    ctx.strokeRect(0.5, 0.5, GRAPH_W - 1, GRAPH_H - 1);
  }
}
